import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    NumericInput
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "NumericInput";

const USAGE_CODE = `const [quantity, setQuantity] = useState("2");

<NumericInput
    spreadBehaviour="stretch"
    onChangeText={setQuantity}
    isShowStepper={true}
    title="Quantity"
    value={quantity}
    suffix="pcs"
    min={1}
    max={10}
/>`;

const VALIDATION_CODE = `<NumericInput
    validation={(text, setText) => {
        if(Number(text) <= 100) {
            return true;
        }

        setText("100");

        return false;
    }}
    onChangeText={setDiscount}
    title="Discount"
    value={discount}
    maxLength={3}
/>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const QUANTITY_MIN = 1;

const QUANTITY_MAX = 10;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("numericInputPage.limitsTitle"),
        id: "limits"
    },
    {
        title: t("numericInputPage.decimalTitle"),
        id: "decimal"
    },
    {
        title: t("numericInputPage.affixesTitle"),
        id: "affixes"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const NumericInputPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        quantity,
        setQuantity
    ] = useState("2");

    return <Article>
        <ComponentHeader
            lead={t("numericInputPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("numericInputPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("numericInputPage.overviewAlternatives")}
            </Paragraph>
            <Preview
                caption={t("numericInputPage.overviewCaption")}
                isStacked={true}
            >
                <NumericInput
                    title={t("numericInputPage.quantity")}
                    suffix={t("numericInputPage.pieces")}
                    onChangeText={setQuantity}
                    spreadBehaviour="stretch"
                    isShowStepper={true}
                    min={QUANTITY_MIN}
                    max={QUANTITY_MAX}
                    value={quantity}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("numericInputPage.limitsTitle")}
            id="limits"
        >
            <Paragraph>
                {t("numericInputPage.limitsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <NumericInput
                    title={t("numericInputPage.weight")}
                    spreadBehaviour="stretch"
                    isShowStepper={true}
                    initialValue="95"
                    suffix="kg"
                    max={100}
                    step={5}
                    min={0}
                />
                <NumericInput
                    title={t("numericInputPage.outOfRange")}
                    suffix={t("numericInputPage.pieces")}
                    spreadBehaviour="stretch"
                    isShowStepper={true}
                    min={QUANTITY_MIN}
                    max={QUANTITY_MAX}
                    initialValue="12"
                />
            </Preview>
            <Paragraph>
                {t("numericInputPage.limitsValidationText")}
            </Paragraph>
            <CodeBlock
                code={VALIDATION_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("numericInputPage.decimalTitle")}
            id="decimal"
        >
            <Paragraph>
                {t("numericInputPage.decimalText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <NumericInput
                    title={t("numericInputPage.rating")}
                    spreadBehaviour="stretch"
                    isShowStepper={true}
                    isDecimal={true}
                    initialValue="3"
                    step={0.5}
                    max={5}
                    min={0}
                />
                <NumericInput
                    title={t("numericInputPage.temperature")}
                    spreadBehaviour="stretch"
                    isShowStepper={true}
                    initialValue="-5"
                    suffix="°C"
                    min={-20}
                    max={40}
                />
            </Preview>
        </Section>
        <Section
            title={t("numericInputPage.affixesTitle")}
            id="affixes"
        >
            <Paragraph>
                {t("numericInputPage.affixesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <NumericInput
                    placeholder={t("numericInputPage.pricePlaceholder")}
                    prefix={t("numericInputPage.currency")}
                    title={t("numericInputPage.price")}
                    spreadBehaviour="stretch"
                    isDecimal={true}
                />
                <NumericInput
                    title={t("numericInputPage.distance")}
                    spreadBehaviour="stretch"
                    initialValue="12"
                    suffix="km"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("numericInputPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <NumericInput
                        spreadBehaviour="stretch"
                        isShowStepper={true}
                        initialValue="3"
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("numericInputPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <NumericInput
                    title={t("numericInputPage.disabled")}
                    spreadBehaviour="stretch"
                    isShowStepper={true}
                    isDisabled={true}
                    initialValue="3"
                />
                <NumericInput
                    title={t("numericInputPage.readOnly")}
                    spreadBehaviour="stretch"
                    isShowStepper={true}
                    isReadOnly={true}
                    initialValue="3"
                />
                <NumericInput
                    title={t("numericInputPage.required")}
                    spreadBehaviour="stretch"
                    isRequired={true}
                    placeholder="0"
                />
                <NumericInput
                    hintText={t("numericInputPage.hintDanger")}
                    spreadBehaviour="stretch"
                    placeholder="0"
                    title="danger"
                    type="danger"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("numericInputPage.a11yLabel"),
                    t("numericInputPage.a11yHint"),
                    t("numericInputPage.a11ySpinbutton"),
                    t("numericInputPage.a11yKeyboard"),
                    t("numericInputPage.a11yButtons"),
                    t("numericInputPage.a11yAffixes"),
                    t("numericInputPage.a11yStates"),
                    t("numericInputPage.a11yKeypad"),
                    t("numericInputPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("numericInputPage.propValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("numericInputPage.propInitialValue"),
                        name: "initialValue",
                        type: "string"
                    },
                    {
                        description: t("numericInputPage.propOnChangeText"),
                        type: "(value: string) => void",
                        name: "onChangeText"
                    },
                    {
                        description: t("numericInputPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("numericInputPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        description: t("numericInputPage.propPlaceholder"),
                        name: "placeholder",
                        type: "string"
                    },
                    {
                        description: t("numericInputPage.propMin"),
                        type: "number",
                        name: "min"
                    },
                    {
                        description: t("numericInputPage.propMax"),
                        type: "number",
                        name: "max"
                    },
                    {
                        description: t("numericInputPage.propStep"),
                        defaultValue: "1",
                        type: "number",
                        name: "step"
                    },
                    {
                        description: t("numericInputPage.propIsDecimal"),
                        defaultValue: "false",
                        name: "isDecimal",
                        type: "boolean"
                    },
                    {
                        description: t("numericInputPage.propIsShowStepper"),
                        defaultValue: "false",
                        name: "isShowStepper",
                        type: "boolean"
                    },
                    {
                        description: t("numericInputPage.propPrefix"),
                        name: "prefix",
                        type: "string"
                    },
                    {
                        description: t("numericInputPage.propSuffix"),
                        name: "suffix",
                        type: "string"
                    },
                    {
                        description: t("numericInputPage.propMaxLength"),
                        name: "maxLength",
                        type: "number"
                    },
                    {
                        type: "(text: string, setText: (text: string) => void) => boolean",
                        description: t("numericInputPage.propValidation"),
                        name: "validation"
                    },
                    {
                        description: t("numericInputPage.propIsWorkWithDebouncer"),
                        name: "isWorkWithDebouncer",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "\"neutral\" | \"danger\" | \"warning\" | \"success\" | \"info\"",
                        description: t("numericInputPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("numericInputPage.propVariant"),
                        type: "\"text\" | \"hidden\"",
                        defaultValue: "\"text\"",
                        name: "variant"
                    },
                    {
                        description: t("numericInputPage.propIsShowHideTextButton"),
                        name: "isShowHideTextButton",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("numericInputPage.propSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        name: "size"
                    },
                    {
                        description: t("numericInputPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("numericInputPage.propIsMinWidthEnabled"),
                        name: "isMinWidthEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("numericInputPage.propIsRequired"),
                        defaultValue: "false",
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("numericInputPage.propIsOptional"),
                        name: "isOptional",
                        type: "boolean"
                    },
                    {
                        description: t("numericInputPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("numericInputPage.propIsShowSubTitle"),
                        name: "isShowSubTitle",
                        type: "boolean"
                    },
                    {
                        description: t("numericInputPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("numericInputPage.propIsReadOnly"),
                        defaultValue: "false",
                        name: "isReadOnly",
                        type: "boolean"
                    },
                    {
                        description: t("numericInputPage.propIsClearEnabled"),
                        name: "isClearEnabled",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("numericInputPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("numericInputPage.propIconOnPress"),
                        name: "iconOnPress",
                        type: "() => void"
                    },
                    {
                        description: t("numericInputPage.propIconAccessibilityLabel"),
                        name: "iconAccessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("numericInputPage.propRightIcon"),
                        type: "NCoreUIKitIcon",
                        name: "rightIcon"
                    },
                    {
                        description: t("numericInputPage.propRightIconOnPress"),
                        name: "rightIconOnPress",
                        type: "() => void"
                    },
                    {
                        description: t("numericInputPage.propRightIconAccessibilityLabel"),
                        name: "rightIconAccessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("numericInputPage.propHintTextIcon"),
                        type: "NCoreUIKitIcon",
                        name: "hintTextIcon"
                    },
                    {
                        description: t("numericInputPage.propIsShowHintTextIcon"),
                        name: "isShowHintTextIcon",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("numericInputPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        description: t("numericInputPage.propCustomColor"),
                        type: "keyof ProjectColorPalette | string",
                        name: "customColor"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("numericInputPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("numericInputPage.propsNote")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("numericInputPage.refNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default NumericInputPage;
